/**
 * Chat Overlay presets and destination guidance (ARCHITECTURE.md §103.13).
 *
 * ==================== WHY THIS LIVES IN THE CONSOLE ====================
 * `apps/web` has no `@connaro/*` dependency at all. That is a deliberate
 * boundary: the Console is an HTTP client, it holds no capability table, and
 * the API is the authority on what a valid configuration is (§103.2a). Every
 * other bounded vocabulary in this editor — fonts, animations, alignment — is
 * written out here as options for the same reason.
 *
 * So the risk is drift, not coupling, and drift is handled where it can
 * actually be caught: `tests/chat-overlay-options.integration.test.ts` imports
 * the real schema and this file and asserts that every value here is one the
 * server would accept, and that no destination is missing guidance. A
 * mismatch fails the build rather than shipping an option the API refuses.
 * ======================================================================
 */

export type DestinationId = 'generic' | 'twitch' | 'youtube' | 'kick' | 'other';

export const DESTINATIONS: readonly { id: DestinationId; label: string }[] = [
  { id: 'generic', label: 'Recording, or generic OBS output' },
  { id: 'twitch', label: 'Twitch' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'kick', label: 'Kick' },
  { id: 'other', label: 'Somewhere else' },
];

export type DestinationGuidance = {
  /** Shown only when the overlay actually merges more than one platform. */
  readonly whenMerged: string;
  /** Whether the operator must acknowledge before the widget can be created. */
  readonly requiresAcknowledgement: boolean;
};

/**
 * Reviewed 2026-09-11; sources in `docs/platforms/public-chat-display.md`.
 *
 * Deliberately unhedged where the review found a clear answer, and
 * deliberately plain where it did not. A warning that sounds legal and cites
 * nothing teaches operators to click past warnings, which costs more than it
 * ever protects.
 */
export const DESTINATION_GUIDANCE: Readonly<Record<DestinationId, DestinationGuidance>> = {
  generic: {
    whenMerged:
      'Nothing to check. A recording or a generic output is not governed by a destination platform’s rules.',
    requiresAcknowledgement: false,
  },
  twitch: {
    whenMerged:
      'Twitch’s guidelines do not permit showing activity from other services on a Twitch broadcast. Twitch does allow a combined tool for your own monitoring. Connaro cannot see where OBS sends this scene, so this one is yours to decide.',
    requiresAcknowledgement: true,
  },
  youtube: {
    whenMerged:
      'YouTube allows this, as long as a viewer cannot be confused about where a message came from. Connaro marks every message with its platform automatically, and that cannot be turned off.',
    requiresAcknowledgement: false,
  },
  kick: {
    whenMerged:
      'Kick’s rule here is about retaining chat, not displaying it. Showing messages on stream is not what that clause covers.',
    requiresAcknowledgement: false,
  },
  other: {
    whenMerged:
      'Check your destination’s current rules on showing activity from other services. Connaro cannot check them for you.',
    requiresAcknowledgement: false,
  },
};

export type ChatOverlayPreset = {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Top-level configuration values. Never platforms, filters or destination. */
  readonly values: Readonly<Record<string, unknown>>;
  /** Merged over the current style rather than replacing it. */
  readonly style: Readonly<Record<string, unknown>>;
};

/**
 * A preset is a BUNDLE OF REAL VALUES, never a stored field.
 *
 * ==================== THE LESSON THIS ENCODES ====================
 * `style.theme` was in the schema from Slice 1, stored on every widget, and
 * drawn by nothing until today. It was a feature in every code path except
 * the one that produces pixels, and it survived review twice.
 *
 * A preset that is only ever an INPUT cannot develop that problem: after it
 * is applied there is nothing left that could disagree with the screen. It
 * also keeps customisation honest — apply Bubble, nudge the font, and the
 * result is simply your configuration, with no preset quietly overriding a
 * value you set and no "modified" badge to explain.
 * ================================================================
 *
 * None of these carry `platforms`, `destination`, `ignoredUsers`,
 * `hideCommands` or `replayCount`. An operator trying a preset to see how it
 * looks must not lose their ignore list — silently, because nothing on screen
 * shows an ignore list.
 */
export const CHAT_OVERLAY_PRESETS: readonly ChatOverlayPreset[] = [
  {
    id: 'minimal',
    name: 'Minimal Transparent',
    description: 'No panel, no background. Built to sit directly over gameplay.',
    values: {
      showAvatar: false,
      showBadges: true,
      showTimestamp: false,
      usernameColor: 'auto',
      messageColor: 'white',
    },
    style: {
      theme: 'minimal',
      backgroundOpacity: 0,
      /*
       * Outline, not shadow, and not both.
       *
       * This preset exists to stay legible over unpredictable video. A shadow
       * lifts text off a busy background but does nothing for white text on a
       * white scene; an outline does. Both together is the heavy glowing look
       * a stream overlay should not have.
       */
      textShadow: false,
      textOutline: true,
      spacing: 6,
      cornerRadius: 0,
      fontScale: 1,
      animation: 'fade',
    },
  },
  {
    id: 'compact',
    name: 'Compact',
    description: 'Tighter lines and smaller text, for a narrow or vertical scene.',
    values: {
      showAvatar: false,
      showBadges: false,
      showTimestamp: false,
      maxMessages: 10,
      usernameColor: 'auto',
      messageColor: 'white',
    },
    style: {
      theme: 'minimal',
      backgroundOpacity: 0,
      textShadow: false,
      textOutline: true,
      spacing: 2,
      cornerRadius: 0,
      fontScale: 0.85,
      animation: 'fade',
    },
  },
  {
    id: 'clean',
    name: 'Clean',
    description: 'A faint backing behind each line. Holds up on the brightest scenes.',
    values: {
      showAvatar: false,
      showBadges: true,
      showTimestamp: false,
      usernameColor: 'auto',
      messageColor: 'white',
    },
    style: {
      theme: 'dark',
      backgroundOpacity: 0.35,
      textShadow: true,
      textOutline: false,
      spacing: 6,
      cornerRadius: 6,
      fontScale: 1,
      animation: 'fade',
    },
  },
  {
    id: 'bubble',
    name: 'Bubble',
    description: 'Rounded bubbles with avatars. Friendlier, and heavier on screen.',
    values: {
      showAvatar: true,
      showBadges: true,
      showTimestamp: false,
      usernameColor: 'auto',
      messageColor: 'white',
    },
    style: {
      theme: 'dark',
      backgroundOpacity: 0.55,
      textShadow: false,
      textOutline: false,
      spacing: 8,
      cornerRadius: 16,
      fontScale: 1,
      animation: 'pop',
    },
  },
  {
    id: 'broadcast',
    name: 'Broadcast',
    description: 'Larger type and timestamps, for a talk show or co-stream layout.',
    values: {
      showAvatar: true,
      showBadges: true,
      showTimestamp: true,
      maxMessages: 8,
      usernameColor: 'auto',
      messageColor: 'white',
    },
    style: {
      theme: 'dark',
      backgroundOpacity: 0.45,
      textShadow: true,
      textOutline: false,
      spacing: 10,
      cornerRadius: 8,
      fontScale: 1.15,
      animation: 'slide',
    },
  },
];

/** Every colour the schema accepts. Bounded there; listed here for the controls. */
export const WIDGET_COLOR_OPTIONS: readonly { id: string; label: string }[] = [
  { id: 'white', label: 'White' },
  { id: 'amber', label: 'Amber' },
  { id: 'green', label: 'Green' },
  { id: 'cyan', label: 'Cyan' },
  { id: 'violet', label: 'Violet' },
  { id: 'pink', label: 'Pink' },
  { id: 'red', label: 'Red' },
];
